import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSession } from '@/app/providers/session-provider';
import { CLIENT_TYPES } from '@/modules/clients/constants/client.constants';
import {
  archiveClient as archivePersistClient,
  deleteClientPermanently as deletePersistClient,
  restoreClient as restorePersistClient,
  listClientsResult,
  listReferralStaffResult,
  createClient as persistClient,
  updateClient as persistClientUpdate,
  loadClientFinancialsResult,
  loadClientActivityResult,
} from '@/modules/clients/services/clients.service';

const emptyClient = {
  name: '',
  company: '',
  email: '',
  phone: '',
  referredBy: '',
  businessName: '',
  vatNumber: '',
  sector: '',
  numeroREA: '',
  codiceFiscale: '',
  formaGiuridica: '',
  indirizzoSedeLegale: '',
  tipoCliente: CLIENT_TYPES.CEDENTE,
  contattiPersonali: { email: '', phone: '' },
  contattiAziendali: { email: '', phone: '' },
  notes: '',
  documenti: [],
};

const ACCOUNTING_AUTH_REQUIRED_ERRORS = new Set(['ACCOUNTING_AUTH_REQUIRED']);

function resolveClientsLoadError(clientsResult, { moduleDisabled, tenantDatabaseBlocked, authRequired }) {
  if (clientsResult.ok) {
    return null;
  }

  if (clientsResult.message) {
    return clientsResult.message;
  }

  if (moduleDisabled) {
    return 'Modulo clienti non abilitato per questo workspace.';
  }

  if (tenantDatabaseBlocked) {
    return 'Database tenant non pronto per il modulo clienti.';
  }

  if (authRequired) {
    return 'Sessione non autorizzata per il catalogo clienti.';
  }

  return 'Backend clienti non disponibile. Elenco clienti non aggiornato.';
}

export function useClientsData(filters) {
  const { logout } = useSession();
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [clientsModuleDisabled, setClientsModuleDisabled] = useState(false);
  const [clientsUnavailable, setClientsUnavailable] = useState(false);
  const [clientsAuthRequired, setClientsAuthRequired] = useState(false);
  const [clientsTenantDatabaseBlocked, setClientsTenantDatabaseBlocked] = useState(false);
  const [staffUnavailable, setStaffUnavailable] = useState(false);
  const [staffAuthRequired, setStaffAuthRequired] = useState(false);
  const [staffError, setStaffError] = useState('');
  const [financialsByClientId, setFinancialsByClientId] = useState({});
  const [financialsMetaByClientId, setFinancialsMetaByClientId] = useState({});
  const [activityByClientId, setActivityByClientId] = useState({});
  const [activityMetaByClientId, setActivityMetaByClientId] = useState({});

  const getFinancials = useCallback((clientId) => {
    if (!clientId) return null;
    return financialsByClientId[clientId] || null;
  }, [financialsByClientId]);

  const getFinancialsMeta = useCallback((clientId) => {
    if (!clientId) {
      return {
        isLoading: false,
        unavailable: false,
      };
    }

    return (
      financialsMetaByClientId[clientId] || {
        isLoading: false,
        unavailable: false,
      }
    );
  }, [financialsMetaByClientId]);

  const loadFinancials = useCallback(async (clientId) => {
    if (!clientId) {
      return {
        ok: true,
        data: { total: 0, received: 0, pending: 0 },
        error: null,
      };
    }

    setFinancialsMetaByClientId((prev) => ({
      ...prev,
      [clientId]: {
        isLoading: true,
        unavailable: false,
      },
    }));

    const result = await loadClientFinancialsResult(clientId);

    if (!result.ok) {
      if (ACCOUNTING_AUTH_REQUIRED_ERRORS.has(result.error) || CLIENTS_AUTH_REQUIRED_ERRORS.has(result.error)) {
        logout();
      }
      setFinancialsMetaByClientId((prev) => ({
        ...prev,
        [clientId]: {
          isLoading: false,
          unavailable: true,
        },
      }));

      return {
        ok: false,
        data: null,
        error: result.error,
      };
    }
    const financials = result.data;

    setFinancialsByClientId((prev) => ({
      ...prev,
      [clientId]: financials,
    }));
    setFinancialsMetaByClientId((prev) => ({
      ...prev,
      [clientId]: {
        isLoading: false,
        unavailable: false,
      },
    }));

    return {
      ok: true,
      data: financials,
      error: null,
    };
  }, []);

  const getClientActivity = useCallback((clientId) => {
    if (!clientId) return null;
    return activityByClientId[clientId] || null;
  }, [activityByClientId]);

  const getClientActivityMeta = useCallback((clientId) => {
    if (!clientId) {
      return {
        isLoading: false,
        status: 'idle',
        unavailable: false,
        authRequired: false,
        error: null,
      };
    }

    return (
      activityMetaByClientId[clientId] || {
        isLoading: false,
        status: 'idle',
        unavailable: false,
        authRequired: false,
        error: null,
      }
    );
  }, [activityMetaByClientId]);

  const loadClientActivity = useCallback(async (clientId) => {
    if (!clientId) {
      return {
        ok: false,
        data: null,
        error: 'CLIENT_ACTIVITY_CLIENT_ID_REQUIRED',
      };
    }

    setActivityMetaByClientId((prev) => ({
      ...prev,
      [clientId]: {
        isLoading: true,
        status: 'loading',
        unavailable: false,
        authRequired: false,
        error: null,
      },
    }));

    const result = await loadClientActivityResult(clientId);

    if (!result.ok) {
      const authRequired = result.error === 'CLIENT_ACTIVITY_AUTH_REQUIRED';
      if (authRequired) {
        logout();
      }
      setActivityMetaByClientId((prev) => ({
        ...prev,
        [clientId]: {
          isLoading: false,
          status: authRequired ? 'auth_required' : 'unavailable',
          unavailable: !authRequired,
          authRequired,
          error: result.error,
        },
      }));

      return result;
    }

    const hasAuthRequiredSection = [
      result.data?.deals?.status,
      result.data?.accounting?.status,
      result.data?.documents?.status,
    ].includes('auth_required');
    const hasUnavailableSection = [
      result.data?.deals?.status,
      result.data?.accounting?.status,
      result.data?.documents?.status,
    ].includes('unavailable');

    setActivityByClientId((prev) => ({
      ...prev,
      [clientId]: result.data,
    }));
    setActivityMetaByClientId((prev) => ({
      ...prev,
      [clientId]: {
        isLoading: false,
        status: result.data?.status || 'available',
        unavailable: hasUnavailableSection,
        authRequired: hasAuthRequiredSection,
        error: result.error,
      },
    }));

    return result;
  }, []);

  useEffect(() => {
    let active = true;

    async function loadClientsContext() {
      const [clientsResult, staffResult] = await Promise.all([
        listClientsResult({ includeArchived: true }),
        listReferralStaffResult(),
      ]);
      if (!active) return;

      const moduleDisabled = clientsResult.error === 'CLIENTS_MODULE_DISABLED';
      const tenantDatabaseBlocked =
        clientsResult.error === 'CLIENTS_TENANT_DATABASE_NOT_READY' ||
        clientsResult.error === 'CLIENTS_TENANT_DATABASE_METADATA_MISSING' ||
        clientsResult.error === 'CLIENTS_TENANT_DATABASE_UNAVAILABLE';
      const authRequired = CLIENTS_AUTH_REQUIRED_ERRORS.has(clientsResult.error);
      const staffAuthRequired = STAFF_AUTH_REQUIRED_ERRORS.has(staffResult.error);
      if (authRequired || staffAuthRequired) {
        logout();
      }
      setClients(clientsResult.data);
      setStaff(staffResult.data);
      setClientsModuleDisabled(moduleDisabled);
      setClientsTenantDatabaseBlocked(tenantDatabaseBlocked);
      setClientsUnavailable(
        !clientsResult.ok &&
          !authRequired &&
          !tenantDatabaseBlocked &&
          !moduleDisabled
      );
      setClientsAuthRequired(authRequired);
      setLoadError(resolveClientsLoadError(clientsResult, { moduleDisabled, tenantDatabaseBlocked, authRequired }));
      setStaffUnavailable(!staffResult.ok && !staffAuthRequired);
      setStaffAuthRequired(staffAuthRequired);
      setStaffError(
        staffResult.ok
          ? ''
          : staffResult.message || 'Catalogo referral staff non disponibile dal backend.'
      );
      setIsLoading(false);
    }

    loadClientsContext();

    return () => {
      active = false;
    };
  }, []);

  const filteredClients = useMemo(() => {
    const search = filters.search.toLowerCase();
    return clients
      .filter((client) => {
        const matchesSearch =
          client.name.toLowerCase().includes(search) || client.company.toLowerCase().includes(search);
        const matchesType =
          filters.tipoCliente === CLIENT_TYPES.ALL || client.tipoCliente === filters.tipoCliente;
        return matchesSearch && matchesType;
      })
      .sort((left, right) =>
        Number(Boolean(left.archived)) - Number(Boolean(right.archived)) ||
        left.name.localeCompare(right.name)
      );
  }, [clients, filters.search, filters.tipoCliente]);

  const counters = useMemo(
    () => ({
      cedenti: clients.filter((c) => !c.archived && c.tipoCliente === CLIENT_TYPES.CEDENTE).length,
      cessionari: clients.filter((c) => !c.archived && c.tipoCliente === CLIENT_TYPES.CESSIONARIO).length,
    }),
    [clients]
  );

  const createClient = async (payload) => {
    const result = await persistClient(payload);
    if (!result.ok) {
      if (CLIENTS_AUTH_REQUIRED_ERRORS.has(result.error)) {
        logout();
      }
      return result;
    }

    setClients((prev) => [...prev, result.data.client].sort((left, right) => left.name.localeCompare(right.name)));
    return result;
  };

  const updateClient = async (payload) => {
    const result = await persistClientUpdate(payload.id, payload);
    if (!result.ok) {
      if (CLIENTS_AUTH_REQUIRED_ERRORS.has(result.error)) {
        logout();
      }
      return result;
    }

    setClients((prev) =>
      prev.map((client) => (client.id === result.data.client.id ? result.data.client : client))
    );
    return result;
  };

  const saveNotes = async (clientId, notes) => {
    const currentClient = clients.find((client) => client.id === clientId);
    if (!currentClient) {
      return {
        ok: false,
        data: null,
        error: 'CLIENT_NOT_FOUND',
        message: 'Cliente non trovato.',
      };
    }

    const result = await persistClientUpdate(clientId, { ...currentClient, notes });
    if (!result.ok) {
      if (CLIENTS_AUTH_REQUIRED_ERRORS.has(result.error)) {
        logout();
      }
      return result;
    }

    setClients((prev) =>
      prev.map((client) => (client.id === result.data.client.id ? result.data.client : client))
    );
    return result;
  };

  const archiveClient = async (clientId) => {
    const result = await archivePersistClient(clientId);
    if (!result.ok) {
      if (CLIENTS_AUTH_REQUIRED_ERRORS.has(result.error)) {
        logout();
      }
      return result;
    }

    setClients((prev) => prev.map((client) => (
      String(client.id) === String(clientId) ? { ...client, archived: true } : client
    )));
    return result;
  };

  const restoreClient = async (clientId) => {
    const result = await restorePersistClient(clientId);
    if (!result.ok) {
      if (CLIENTS_AUTH_REQUIRED_ERRORS.has(result.error)) {
        logout();
      }
      return result;
    }

    setClients((prev) =>
      prev.map((client) => (client.id === result.data.client.id ? result.data.client : client))
    );
    return result;
  };

  const deleteClientPermanently = async (clientId) => {
    const result = await deletePersistClient(clientId);
    if (result.ok) setClients((prev) => prev.filter((client) => String(client.id) !== String(clientId)));
    return result;
  };

  return {
    staff,
    staffUnavailable,
    staffAuthRequired,
    staffError,
    staffCatalogStatus: staffAuthRequired
      ? 'auth_required'
      : staffUnavailable
        ? 'unavailable'
        : staff.length > 0
          ? 'available'
          : 'empty',
    clients,
    clientsModuleDisabled,
    clientsTenantDatabaseBlocked,
    clientsUnavailable,
    clientsCatalogStatus: clientsAuthRequired
      ? 'auth_required'
      : clientsModuleDisabled
        ? 'module_disabled'
        : clientsTenantDatabaseBlocked
          ? 'tenant_database_unavailable'
        : clientsUnavailable
          ? 'unavailable'
          : clients.length > 0
            ? 'available'
            : 'empty',
    filteredClients,
    counters,
    emptyClient,
    createClient,
    updateClient,
    archiveClient,
    restoreClient,
    deleteClientPermanently,
    saveNotes,
    getFinancials,
    getFinancialsMeta,
    loadFinancials,
    getClientActivity,
    getClientActivityMeta,
    loadClientActivity,
    isLoading,
    loadError,
    clientsAuthRequired,
  };
}
const CLIENTS_AUTH_REQUIRED_ERRORS = new Set([
  'CLIENTS_AUTH_REQUIRED',
  'CLIENTS_SESSION_REFRESH_REQUIRED',
  'CLIENTS_SESSION_EXPIRED',
]);

const STAFF_AUTH_REQUIRED_ERRORS = new Set([
  'STAFF_AUTH_REQUIRED',
  'STAFF_SESSION_REFRESH_REQUIRED',
  'STAFF_SESSION_EXPIRED',
]);
